import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BigNumber } from 'ethers'
import isNil from 'lodash/isNil'
import { License, LicenseType } from '@mprolab/sdk'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { useFeeVaultContract, useFeeVaultWriteContract } from 'hooks/useContract'
import { useFeeVaultContractAddress, useLifetimeTokens } from 'state/global/hooks'
import { useGetLicensesQuery } from 'state/services/indexNode/indexNode'
import { getMulticallResponse } from 'utils/getMulticallResponse'
import { reportClientError } from 'utils/reportClientError'
import { ascending, completeness, CompletenessVerdict, errorNameOf } from './feeVault.rules'

/**
 * Everything the Fee Vault section shows, and the one thing it does.
 *
 * Reads go through Multicall3 in one round trip; the claim is a wallet
 * transaction preceded by a dry run (`callStatic.claim`) so a refusal becomes
 * a sentence before any wallet prompt appears. No backend is in the path: the
 * registry supplies the wallet's licence ids, the chain answers everything
 * else, and the contract is the ledger.
 */

export interface FeeVaultTotals {
  /** Everything that ever arrived — the community's earnings. */
  collected: BigNumber
  /** What holders have taken out. */
  claimed: BigNumber
  /** Attributed and waiting to be claimed. */
  held: BigNumber
  shareCount: number
  paused: boolean
  licence: string
}

export interface FeeVaultLicenceRow {
  id: string
  isShare: boolean
  claimable: BigNumber
  claimed: BigNumber
  lastClaimAt: number
  lastClaimedBy: string
}

export interface FeeVaultWalletView {
  /** iNODE ids the registry knows for this wallet, ascending. */
  ids: string[]
  rows: FeeVaultLicenceRow[]
  /** What claim() would pay right now, or null when the preview was refused. */
  previewTotal: BigNumber | null
  previewError?: string
  verdict: CompletenessVerdict
  /** What the collection says this wallet holds. */
  onChainCount: number
}

export type ClaimOutcome =
  | { status: 'sent'; hash: string }
  | { status: 'confirmed'; hash: string; total: BigNumber }
  | { status: 'refused'; errorName?: string; reason?: string }

const ERC721_BALANCE_OF = ['function balanceOf(address) view returns (uint256)']

export function useFeeVault() {
  const { account, chainId: walletChainId } = useWeb3React()
  const contracts = useFeeVaultContract()
  const writeContracts = useFeeVaultWriteContract()
  const registered = useFeeVaultContractAddress()
  const lifetimeTokens = useLifetimeTokens()
  const accessToken = account ? lifetimeTokens?.[account] : undefined

  // One vault today — the iNODE one. A second collection is a second row.
  const vault = contracts[0]
  const writeVault = writeContracts[0]
  const entry = registered[0]
  const chainId = entry?.chainId

  const licences = useGetLicensesQuery(
    { account: account as string, accessToken: accessToken as string },
    { skip: !account || !accessToken, refetchOnMountOrArgChange: true }
  )

  const [totals, setTotals] = useState<FeeVaultTotals | null>(null)
  const [walletView, setWalletView] = useState<FeeVaultWalletView | null>(null)
  const [loading, setLoading] = useState(true)
  const [unavailable, setUnavailable] = useState(false)
  const [claiming, setClaiming] = useState(false)
  const generation = useRef(0)

  /*
   * The wallet's iNODE licences, from the registry. Only the type we pay for
   * — ggNODE and MGA are other collections and, one day, other vaults.
   */
  const [licenceAddress, setLicenceAddress] = useState<string | null>(null)
  const inode = useMemo(() => {
    const all = (licences.data ?? []) as License[]
    /*
     * Which licences belong to THIS vault. The registry also knows iNODE
     * licences on the other chain's collection, and the type alone does not
     * tell them apart — the vault's own licence address does. Until it is
     * read, fall back to the type so the first paint is not empty.
     */
    const mine = all.filter((l) => {
      if (l.type !== LicenseType.MGA_INODE) return false
      if (!licenceAddress || !l.nftContract) return true
      return String(l.nftContract).toLowerCase() === licenceAddress.toLowerCase()
    })
    const withId = mine.filter((l) => !isNil(l.tokenId) && String(l.tokenId).trim() !== '')
    return {
      registered: mine.length,
      ids: ascending(withId.map((l) => String(l.tokenId)))
    }
  }, [licences.data, licenceAddress])

  const refresh = useCallback(async () => {
    if (!vault || !entry) {
      setLoading(false)
      return
    }
    const gen = ++generation.current
    try {
      const iface = vault.interface
      const target = vault.address
      const base = [
        { target, allowFailure: false, callData: iface.encodeFunctionData('collectedTotal') },
        { target, allowFailure: false, callData: iface.encodeFunctionData('claimedTotal') },
        { target, allowFailure: false, callData: iface.encodeFunctionData('heldForHolders') },
        { target, allowFailure: false, callData: iface.encodeFunctionData('shareCount') },
        { target, allowFailure: false, callData: iface.encodeFunctionData('paused') },
        { target, allowFailure: false, callData: iface.encodeFunctionData('licence') }
      ]
      const results = await getMulticallResponse({ chainId, calls: base })
      if (gen !== generation.current) return
      // Typechain narrows the name to a union; the calls above are the same names.
      const dec = (i: number, fn: string) => iface.decodeFunctionResult(fn as never, results[i].returnData)[0]
      const next: FeeVaultTotals = {
        collected: dec(0, 'collectedTotal') as BigNumber,
        claimed: dec(1, 'claimedTotal') as BigNumber,
        held: dec(2, 'heldForHolders') as BigNumber,
        shareCount: (dec(3, 'shareCount') as BigNumber).toNumber(),
        paused: dec(4, 'paused') as boolean,
        licence: String(dec(5, 'licence'))
      }
      setTotals(next)
      setLicenceAddress(next.licence)
      setUnavailable(false)

      if (!account || !accessToken) {
        setWalletView(null)
        return
      }

      // The wallet's side: how many it holds on chain, and each id's state.
      const { Interface } = await import('ethers/lib/utils')
      const erc721 = new Interface(ERC721_BALANCE_OF)
      const ids = inode.ids
      const perId = ids.flatMap((id) => [
        { target, allowFailure: true, callData: iface.encodeFunctionData('isShare', [id]) },
        { target, allowFailure: true, callData: iface.encodeFunctionData('claimable', [id]) },
        { target, allowFailure: true, callData: iface.encodeFunctionData('ledgerOf', [id]) }
      ])
      const walletCalls = [
        { target: next.licence, allowFailure: true, callData: erc721.encodeFunctionData('balanceOf', [account]) },
        ...(ids.length
          ? [{ target, allowFailure: true, callData: iface.encodeFunctionData('previewClaim', [account, ids]) }]
          : []),
        ...perId
      ]
      const wr = await getMulticallResponse({ chainId, calls: walletCalls })
      if (gen !== generation.current) return

      if (!wr[0].success) {
        // Unreadable is not "zero" and not a number to put in a sentence.
        setUnavailable(true)
        return
      }
      const onChainCount = (erc721.decodeFunctionResult('balanceOf', wr[0].returnData)[0] as BigNumber).toNumber()
      let previewTotal: BigNumber | null = null
      let previewError: string | undefined
      let offset = 1
      if (ids.length) {
        const p = wr[1]
        offset = 2
        if (p.success) previewTotal = iface.decodeFunctionResult('previewClaim', p.returnData)[0] as BigNumber
        else {
          try {
            previewError = iface.parseError(p.returnData).name
          } catch {
            previewError = 'unknown'
          }
        }
      }
      const rows: FeeVaultLicenceRow[] = ids.map((id, i) => {
        const [s, c, l] = [wr[offset + i * 3], wr[offset + i * 3 + 1], wr[offset + i * 3 + 2]]
        const isShare = s.success ? (iface.decodeFunctionResult('isShare', s.returnData)[0] as boolean) : false
        const claimable = c.success ? (iface.decodeFunctionResult('claimable', c.returnData)[0] as BigNumber) : BigNumber.from(0)
        const ledger = l.success ? iface.decodeFunctionResult('ledgerOf', l.returnData)[0] : null
        return {
          id,
          isShare,
          claimable,
          claimed: ledger ? (ledger.claimed as BigNumber) : BigNumber.from(0),
          lastClaimAt: ledger ? Number(ledger.lastClaimAt) : 0,
          lastClaimedBy: ledger ? String(ledger.lastClaimedBy) : ''
        }
      })
      setWalletView({
        ids,
        rows,
        previewTotal,
        previewError,
        onChainCount,
        verdict: completeness({ known: ids.length, registered: inode.registered, onChain: onChainCount })
      })
    } catch (error) {
      if (gen !== generation.current) return
      // Unreadable is not "zero". The section says it could not read, and
      // keeps whatever it last knew.
      setUnavailable(true)
      reportClientError({ kind: 'wallet', message: `feevault:read:${(error as Error)?.message ?? 'failed'}`, handled: true })
    } finally {
      if (gen === generation.current) setLoading(false)
    }
  }, [vault, entry, chainId, account, accessToken, inode.ids, inode.registered])

  useEffect(() => {
    void refresh()
    const id = setInterval(() => void refresh(), 30_000)
    return () => clearInterval(id)
  }, [refresh])

  /**
   * Claim for every licence, after a dry run.
   *
   * The dry run is the whole point: a refusal comes back as the contract's
   * own custom error, which becomes a sentence, and no wallet opens for a
   * transaction that cannot succeed.
   */
  const claim = useCallback(async (): Promise<ClaimOutcome> => {
    if (!writeVault || !walletView || !account) return { status: 'refused', reason: 'not-ready' }
    const ids = walletView.ids
    setClaiming(true)
    try {
      try {
        await writeVault.callStatic.claim(ids)
      } catch (error) {
        const errorName = errorNameOf(error)
        reportClientError({ kind: 'transaction', message: `feevault:claim:dry-run:${errorName ?? (error as Error)?.message}`, handled: true })
        return { status: 'refused', errorName, reason: (error as Error)?.message }
      }
      const tx = await writeVault.claim(ids)
      const receipt = await tx.wait()
      const total = walletView.previewTotal ?? BigNumber.from(0)
      await refresh()
      return receipt.status === 1
        ? { status: 'confirmed', hash: tx.hash, total }
        : { status: 'refused', reason: 'reverted', errorName: undefined }
    } catch (error) {
      const errorName = errorNameOf(error)
      reportClientError({ kind: 'transaction', message: `feevault:claim:${errorName ?? (error as Error)?.message}`, handled: true })
      return { status: 'refused', errorName, reason: (error as Error)?.message }
    } finally {
      setClaiming(false)
    }
  }, [writeVault, walletView, account, refresh])

  return {
    configured: Boolean(entry),
    chainId,
    onRightChain: !isNil(chainId) && walletChainId === chainId,
    address: entry?.address,
    totals,
    walletView,
    signedIn: Boolean(account && accessToken),
    licencesLoading: licences.isLoading,
    loading,
    unavailable,
    claiming,
    refresh,
    claim
  }
}
